import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { RESUME_LIBRARY_LABEL } from '../utils/uxLabels';

const ChatInput = ({
  onSend,
  placeholder = 'On your command!',
  value: controlledValue,
  onChange: controlledOnChange,
  inputRef,
  mode: controlledMode,
  onModeChange,
  onAttachmentClick,
  onUploadJD,      // callback when user picks "Upload JD"
  hasJd = false,   // true when the current session already has a JD
  jdChecking = false, // true while session is loading (disable button until we know)
  showFilterMode = true,
})  => {
  const { settings } = useSettings();
  const [internalMessage, setInternalMessage] = useState('');
  const [internalMode, setInternalMode] = useState('agent'); // "agent" or "ask"
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const plusMenuRef = useRef(null);
  const primaryColor = settings.theme?.colors?.primary || '#3B5BDB';
  const secondaryColor = settings.theme?.colors?.secondary || '#20C997';

  // Use controlled value if provided, otherwise use internal state
  const message = controlledValue !== undefined ? controlledValue : internalMessage;
  const setMessage = controlledOnChange || setInternalMessage;

  // Use controlled mode if provided, otherwise use internal state
  const currentMode = controlledMode !== undefined ? controlledMode : internalMode;
  const setMode = onModeChange || setInternalMode;

  // Auto-resize textarea up to 4 lines
  const autoResize = useCallback(() => {
    const textarea = inputRef?.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const lineHeight = 20; // approximate line height in px
    const maxHeight = lineHeight * 4; // 4 lines max
    textarea.style.height = Math.min(textarea.scrollHeight, maxHeight) + 'px';
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [inputRef]);

  useEffect(() => {
    autoResize();
  }, [message, autoResize]);
  // Close plus menu on outside click
  useEffect(() => {
    if (!showPlusMenu) return;
    const handler = (e) => {
      if (plusMenuRef.current && !plusMenuRef.current.contains(e.target)) {
        setShowPlusMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPlusMenu]);

  const doSubmit = () => {
    const trimmed = (message || '').trim();
    if (!trimmed) return;
    onSend(trimmed, currentMode);
    setMessage('');
    // Reset textarea height after sending
    if (inputRef?.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.overflowY = 'hidden';
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    doSubmit();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Allow newline inside the input
        return;
      }
      // Plain Enter should submit instead of creating a new line
      e.preventDefault();
      doSubmit();
    }
  };

  const PlusIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );

  const MicrophoneIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 11C19 14.866 15.866 18 12 18M12 18C8.13401 18 5 14.866 5 11M12 18V22M12 22H8M12 22H16M12 14C10.3431 14 9 12.6569 9 11V5C9 3.34315 10.3431 2 12 2C13.6569 2 15 3.34315 15 5V11C15 12.6569 13.6569 14 12 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const SendIcon = () => (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
  );

  // Agent mode icon — rocket/bolt for action execution
  const AgentIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  );

  // Filter mode icon — funnel for candidate filtering
  const FilterIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
    </svg>
  );

  return (
    <form onSubmit={handleSubmit} className="w-full">
      {/* Mode Toggle */}
      <div className="flex items-center gap-1 mb-2 px-1">
        <button
          type="button"
          onClick={() => setMode('agent')}
          className={`chat-mode-toggle ${currentMode === 'agent' ? 'chat-mode-toggle-active' : 'chat-mode-toggle-inactive'}`}
          title="Agent mode: Execute actions — send emails, schedule interviews, create JDs"
        >
          <AgentIcon />
          <span>Agent</span>
        </button>
        {showFilterMode && (
          <button
            type="button"
            onClick={() => setMode('ask')}
            className={`chat-mode-toggle ${currentMode === 'ask' ? 'chat-mode-toggle-active-ask' : 'chat-mode-toggle-inactive'}`}
            title="Filter mode: Explore and filter candidates, view analytics"
          >
            <FilterIcon />
            <span>Filter</span>
          </button>
        )}
        <span className="chat-mode-hint">
          {currentMode === 'agent' ? 'Actions will be executed' : 'Filter and explore candidates'}
        </span>
      </div>

      <div className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl px-4 py-3 shadow-sm">
        {/* Plus Icon — shows popup with Upload JD */}
        <div className="relative flex-shrink-0" ref={plusMenuRef}>
          <button
            type="button"
            onClick={() => setShowPlusMenu((v) => !v)}
            className="flex-shrink-0 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-250 self-end"
            aria-label="Upload Job Description"
            title="Upload Job Description"
          >
            <PlusIcon />
          </button>

          {/* Popup — single option: Upload JD */}
          {showPlusMenu && (
            <div className="absolute bottom-full left-0 mb-2 w-52 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 z-50 overflow-hidden">
              <button
                type="button"
                disabled={hasJd || jdChecking}
                onClick={() => {
                  setShowPlusMenu(false);
                  if (!hasJd && !jdChecking && onUploadJD) onUploadJD();
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition-colors ${
                  (hasJd || jdChecking)
                    ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed bg-gray-50 dark:bg-gray-800/50'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer'
                }`}
                title={hasJd ? 'JD already created for this session' : jdChecking ? 'Loading session...' : 'Upload a PDF or Word Job Description'}
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="flex-1">Upload JD</span>
                {hasJd && !jdChecking && <span className="text-xs text-green-500 font-medium">Done</span>}
                {jdChecking && <span className="text-xs text-gray-400">...</span>}
              </button>
            </div>
          )}
        </div>

        {/* Input Field */}
        <textarea
          ref={inputRef}
          value={message || ''}
          onChange={(e) => {
            setMessage(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          rows={1}
          placeholder={currentMode === 'ask' ? 'Search pipeline... (e.g., "who knows React?", "from Pune")' : placeholder}
          className="flex-1 min-w-0 bg-transparent border-0 outline-none ring-0 focus:ring-0 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm resize-none"
          style={{
            lineHeight: '20px',
            maxHeight: '80px',
            overflowY: 'hidden',
            paddingTop: '2px',
            paddingBottom: '2px',
          }}
        />

        {/* Microphone Icon */}
        <button
          type="button"
          className="flex-shrink-0 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-250 self-end"
          aria-label="Voice input"
        >
          <MicrophoneIcon />
        </button>

        {/* Send Button */}
        <button
          type="submit"
          disabled={!message.trim()}
          className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed self-end"
          style={{
            background: message.trim()
              ? `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`
              : `linear-gradient(135deg, ${primaryColor}60, ${secondaryColor}60)`,
          }}
          aria-label="Send message"
        >
          <SendIcon />
        </button>
      </div>
    </form>
  );
};

export default ChatInput;
